/**
 * CheckinLocationList Component
 *
 * Lista de locais (recursos) disponíveis para check-in.
 * Mostra distância, nome e endereço de cada local.
 *
 * @since 2025-12-17
 * @module tmf_checkin
 */

import { useMemo } from 'react';
import clsx from 'clsx';
import type { NearbyLocation } from '@/types/checkin';
import { formatDistance } from '@/services/checkinApi';

interface CheckinLocationListProps {
  locations: NearbyLocation[];
  loading?: boolean;
  error?: string | null;
  onSelect: (location: NearbyLocation) => void;
  selectedId?: number | null;
  emptyMessage?: string;
}

/**
 * Formata o endereço completo de um local
 */
function formatAddress(address: NearbyLocation['address']): string {
  if (!address) return '';

  const parts = [
    address.address,
    address.number,
    address.complement,
  ].filter(Boolean);

  const street = parts.join(', ');

  const cityState = [address.city, address.state].filter(Boolean).join(' - ');

  return [street, address.neighborhood, cityState].filter(Boolean).join(', ');
}

export function CheckinLocationList({
  locations,
  loading = false,
  error = null,
  onSelect,
  selectedId = null,
  emptyMessage = 'Nenhum local encontrado',
}: CheckinLocationListProps) {
  // Ordena por distância
  const sortedLocations = useMemo(() => {
    return [...locations].sort((a, b) => a.distance_meters - b.distance_meters);
  }, [locations]);

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mb-3" />
        <p className="text-sm text-muted-foreground">Buscando locais próximos...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="w-12 h-12 bg-red-100 dark:bg-red-500/20 rounded-full flex items-center justify-center mb-3">
          <i className="bi bi-exclamation-triangle text-red-500 dark:text-red-400 text-xl" />
        </div>
        <p className="text-sm text-red-600 dark:text-red-400 text-center">{error}</p>
      </div>
    );
  }

  // Empty state
  if (sortedLocations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mb-3">
          <i className="bi bi-geo-alt text-muted-foreground text-xl" />
        </div>
        <p className="text-sm text-muted-foreground text-center">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-border">
      {sortedLocations.map((location) => {
        const isSelected = selectedId === location.id;
        const address = formatAddress(location.address);
        const distance = formatDistance(location.distance_meters);

        return (
          <button
            key={location.id}
            type="button"
            onClick={() => onSelect(location)}
            className={clsx(
              'w-full text-left p-4 hover:bg-muted transition-colors',
              isSelected && 'bg-orange-50 dark:bg-orange-500/20 hover:bg-orange-50 dark:hover:bg-orange-500/20'
            )}
          >
            <div className="flex items-start gap-3">
              {/* Distance badge */}
              <div
                className={clsx(
                  'flex-shrink-0 px-2 py-1 rounded-full text-xs font-medium',
                  isSelected
                    ? 'bg-orange-500 text-white'
                    : 'bg-muted text-muted-foreground'
                )}
              >
                {distance}
              </div>

              {/* Location info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h4
                    className={clsx(
                      'font-medium truncate',
                      isSelected ? 'text-orange-700 dark:text-orange-400' : 'text-foreground'
                    )}
                  >
                    {location.resource_name}
                  </h4>
                  {location.code && (
                    <span className="text-xs text-muted-foreground flex-shrink-0">
                      #{location.code}
                    </span>
                  )}
                </div>

                {address && (
                  <p className="text-sm text-muted-foreground mt-0.5 truncate">
                    {address}
                  </p>
                )}

                {/* Check-in status indicator */}
                {location.has_active_checkin && (
                  <div className="flex items-center gap-1 mt-1.5">
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <span className="text-xs text-green-600 dark:text-green-400">Check-in ativo</span>
                  </div>
                )}
              </div>

              {/* Chevron */}
              <div className="flex-shrink-0 self-center">
                <i
                  className={clsx(
                    'bi bi-chevron-right text-sm',
                    isSelected ? 'text-orange-500 dark:text-orange-400' : 'text-muted-foreground'
                  )}
                />
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

export default CheckinLocationList;
